import { useAtomValue } from 'jotai';
import { lazy, Suspense } from 'react';
import { Route, Routes, useNavigate } from 'react-router-dom';
import { OktaAuth } from '@okta/okta-auth-js'; // Correct import
import { LoginCallback, Security } from '@okta/okta-react';

import {
  ChatConnectionProvider,
  ConnectionProvider,
  FeatureFlag,
  FeatureFlagProvider,
  SocketProvider,
} from '@frontend/contexts';

import CanUser from './components/CanUser';
import CanUserWithFeatureFlag from './components/CanUserWithFeatureFlag';
import { ErrorBoundary } from './components/error-boundary';
import FullScreenLoader from './components/FullScreenLoader';
import { OptiGlobalHeader } from './components/optiGlobalHeader';
import PathNavigator from './components/PathNavigator';
import RequireAuth from './components/RequireAuth'; // Custom auth wrapper for protected pages
import { BaseLayout } from './layouts/baseLayout';
import { SentryErrorBoundary } from './utils/sentry';
import { globalOptiHeaderVisibleAtom } from './atoms';
import { useFontSize, useInstanceId, useTheme } from './hooks';
import oktaAuth from './oktaAuth';

const AgentPage = lazy(() =>
  import(/* webpackChunkName: "AgentPage" */ './features').then(m => ({ default: m.AgentPage }))
);
const Chat = lazy(() =>
  import(/* webpackChunkName: "Chat" */ './features/chat').then(m => ({ default: m.Chat }))
);
const Instances = lazy(() =>
  import(/* webpackChunkName: "Instances" */ './features/instances').then(m => ({
    default: m.Instances,
  }))
);
const InstructionPage = lazy(() =>
  import(/* webpackChunkName: "InstructionPage" */ './features/userInstructions/components').then(
    m => ({ default: m.InstructionPage })
  )
);
const RAGConfiguration = lazy(() =>
  import(/* webpackChunkName: "RAGConfiguration" */ './features/rag').then(m => ({
    default: m.RAGConfiguration,
  }))
);
const SettingsPage = lazy(
  () => import(/* webpackChunkName: "SettingsPage" */ './features/settings/Settings')
);
const ToolsManagement = lazy(() =>
  import(/* webpackChunkName: "ToolsManagement" */ './features').then(m => ({
    default: m.ToolsManagement,
  }))
);
const WorkflowExecutionPage = lazy(() =>
  import(
    /* webpackChunkName: "WorkflowExecutionPage" */ './features/agents/components/workflow-execution'
  ).then(m => ({
    default: m.WorkflowExecutionPage,
  }))
);
const ToolsWarningBanner = lazy(() =>
  import(
    /* webpackChunkName: "ToolsWarningBanner" */ './features/tools-management/components/ToolsWarningBanner/ToolsWarningBanner'
  ).then(m => ({
    default: m.ToolsWarningBanner,
  }))
);

function App() {
  return (
    <SentryErrorBoundary fallback={<p>An error has occurred</p>}>
      <WrappedSecurity />
    </SentryErrorBoundary>
  );
}

function WrappedSecurity() {
  const navigate = useNavigate();
  const { instanceId } = useInstanceId();
  const globalOptiHeaderVisible = useAtomValue(globalOptiHeaderVisibleAtom);
  useTheme();
  useFontSize();
  const restoreOriginalUri = async (_oktaAuth: OktaAuth, originalUri?: string): Promise<void> => {
    navigate(originalUri || '/');
  };

  return (
    <Security oktaAuth={oktaAuth} restoreOriginalUri={restoreOriginalUri}>
      <Routes>
        <Route path="/login" element={<LoginCallback />} />
        <Route
          path="/instances/:instanceId"
          element={
            <RequireAuth>
              <FeatureFlagProvider>
                <ErrorBoundary>
                  <OptiGlobalHeader visible={globalOptiHeaderVisible} />
                </ErrorBoundary>
                <SocketProvider>
                  <Suspense fallback={null}>
                    <ToolsWarningBanner />
                  </Suspense>
                  <BaseLayout />
                </SocketProvider>
              </FeatureFlagProvider>
            </RequireAuth>
          }
        >
          <Route
            path="chat/*"
            element={
              <CanUser operations={['allow_chat']}>
                <ChatConnectionProvider>
                  <Suspense fallback={<FullScreenLoader />}>
                    <Chat />
                  </Suspense>
                </ChatConnectionProvider>
              </CanUser>
            }
          />
          <Route
            path="instructions/*"
            element={
              <CanUser operations={['view_instructions', 'manage_instructions']}>
                <ConnectionProvider>
                  <Suspense fallback={<FullScreenLoader />}>
                    <InstructionPage />
                  </Suspense>
                </ConnectionProvider>
              </CanUser>
            }
          />
          <Route
            path="connections/*"
            element={
              <CanUser operations={['manage_instances']}>
                <Suspense fallback={<FullScreenLoader />}>
                  <Instances />
                </Suspense>
              </CanUser>
            }
          />
          <Route
            path="tools/*"
            element={
              <CanUser operations={['allow_chat']}>
                <Suspense fallback={<FullScreenLoader />}>
                  <ToolsManagement />
                </Suspense>
              </CanUser>
            }
          />
          <Route
            path="settings/*"
            element={
              <CanUserWithFeatureFlag
                featureKey="appearance_settings"
                enabledPermissions={['allow_chat']}
                disabledPermissions={['manage_instances']}
              >
                <Suspense fallback={<FullScreenLoader />}>
                  <SettingsPage />
                </Suspense>
              </CanUserWithFeatureFlag>
            }
          />
          <Route
            path="agents/workflow/executions/:executionId/*"
            element={
              <CanUser operations={['allow_chat']}>
                <Suspense fallback={<FullScreenLoader />}>
                  <WorkflowExecutionPage />
                </Suspense>
              </CanUser>
            }
          />
          <Route
            path="agents/*"
            element={
              <CanUser operations={['manage_instances']}>
                <Suspense fallback={<FullScreenLoader />}>
                  <AgentPage />
                </Suspense>
              </CanUser>
            }
          />
          <Route
            path="rag/*"
            element={
              <CanUser operations={['manage_rag_config', 'manage_instructions']}>
                <FeatureFlag featureKey="shamwow-settings-page">
                  <Suspense fallback={<FullScreenLoader />}>
                    <RAGConfiguration />
                  </Suspense>
                </FeatureFlag>
              </CanUser>
            }
          />
          <Route path="" element={<PathNavigator path={`/instances/${instanceId}/chat`} />} />
        </Route>
        <Route
          path="*"
          element={
            <RequireAuth>
              <PathNavigator path={`/instances/${instanceId}/chat`} />
            </RequireAuth>
          }
        />
      </Routes>
    </Security>
  );
}

export default App;
